import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BrandLogo } from "@/components/shared/BrandLogo";
import {
  GripVertical, Lock, Unlock, Clock, Sun, Coffee, Utensils,
  Sunset, ChevronLeft, ChevronRight, RotateCcw, Wand2, DollarSign,
} from "lucide-react";
import SchedulerHelpSheet from "./SchedulerHelpSheet";

const SLOT_OPTIONS = [
  { id: "morning", label: "Morning", icon: Coffee, color: "bg-amber-100 text-amber-700" },
  { id: "lunch", label: "Lunch", icon: Utensils, color: "bg-orange-100 text-orange-700" },
  { id: "afternoon", label: "Afternoon", icon: Sun, color: "bg-sky-100 text-sky-700" },
  { id: "evening", label: "Evening", icon: Sunset, color: "bg-indigo-100 text-indigo-700" },
];

const SLOT_ORDER = { morning: 0, lunch: 1, afternoon: 2, evening: 3 };

const CAT_SLOT = {
  "Cafes & Drinks": "morning",
  "Bakery": "morning",
  "Fast Food": "lunch",
  "Restaurant": "evening",
  "Dessert & Ice Cream": "afternoon",
  "Retail": "afternoon",
  "Clothing & Apparel": "afternoon",
  "Entertainment": "evening",
  "Beauty & Skincare": "afternoon",
  "Grocery": "morning",
  "Health & Fitness": "morning",
  "Pet": "afternoon",
  "Jewelry & Accessories": "afternoon",
  "Auto": "morning",
  "Home & Garden": "afternoon",
};

export default function SmartQueueScheduler({ itinerary, onUpdate, themeColor }) {
  const numDays = itinerary?.num_days || 1;
  const [activeDay, setActiveDay] = useState(1);
  const [startHour, setStartHour] = useState(9);

  const [queue, setQueue] = useState(() =>
    (itinerary?.stops || []).map(s => ({
      ...s,
      time_slot: s.time_slot || CAT_SLOT[s.category] || "afternoon",
      locked: false,
      day: s.day || 1,
    }))
  );

  const dayStops = queue.filter(s => s.day === activeDay).sort((a, b) => SLOT_ORDER[a.time_slot] - SLOT_ORDER[b.time_slot]);

  const moveStop = useCallback((fromIdx, direction) => {
    setQueue(prev => {
      const dayItems = prev.filter(s => s.day === activeDay).sort((a, b) => SLOT_ORDER[a.time_slot] - SLOT_ORDER[b.time_slot]);
      const otherItems = prev.filter(s => s.day !== activeDay);
      const toIdx = fromIdx + direction;
      if (toIdx < 0 || toIdx >= dayItems.length) return prev;
      // Swap
      const temp = dayItems[fromIdx];
      dayItems[fromIdx] = dayItems[toIdx];
      dayItems[toIdx] = temp;
      return [...otherItems, ...dayItems];
    });
  }, [activeDay]);

  const toggleLock = useCallback((businessId) => {
    setQueue(prev => prev.map(s => s.business_id === businessId ? { ...s, locked: !s.locked } : s));
  }, []);

  const changeSlot = useCallback((businessId, newSlot) => {
    setQueue(prev => prev.map(s => s.business_id === businessId ? { ...s, time_slot: newSlot } : s));
  }, []);

  const changeDayForStop = useCallback((businessId, newDay) => {
    setQueue(prev => prev.map(s => s.business_id === businessId ? { ...s, day: newDay } : s));
  }, []);

  const autoArrange = useCallback(() => {
    setQueue(prev => {
      const locked = prev.filter(s => s.locked && s.day === activeDay);
      const unlocked = prev.filter(s => !s.locked && s.day === activeDay);
      const other = prev.filter(s => s.day !== activeDay);
      // Auto-assign time slots based on category
      const arranged = unlocked.map(s => ({
        ...s,
        time_slot: CAT_SLOT[s.category] || "afternoon",
      }));
      return [...other, ...locked, ...arranged];
    });
  }, [activeDay]);

  const totalValue = dayStops.reduce((a, s) => a + (s.value_est || 0), 0);

  // Compute estimated times
  const getEstTime = (idx) => {
    let mins = startHour * 60;
    for (let i = 0; i < idx; i++) {
      mins += 15 + 10; // 15 min avg stop + 10 min drive
    }
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h > 12 ? h - 12 : h}:${String(m).padStart(2, "0")}${h >= 12 ? "pm" : "am"}`;
  };

  return (
    <div className="space-y-4" data-testid="smart-queue-scheduler">
      {/* Day selector */}
      {numDays > 1 && (
        <div className="flex items-center justify-center gap-3 mb-2">
          <button onClick={() => setActiveDay(d => Math.max(1, d - 1))} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm font-bold text-slate-700">Day {activeDay} of {numDays}</span>
          <button onClick={() => setActiveDay(d => Math.min(numDays, d + 1))} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}

      {/* Controls bar */}
      <div className="flex items-center justify-between bg-white rounded-xl p-3 border border-slate-100 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="text-center">
            <p className="text-lg font-black text-slate-800">{dayStops.length}</p>
            <p className="text-[10px] text-slate-400">Stops</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-black text-green-600">${totalValue}</p>
            <p className="text-[10px] text-slate-400">Value</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Start time picker */}
          <div className="flex items-center gap-1 bg-slate-50 rounded-lg px-2 py-1">
            <Clock className="w-3 h-3 text-slate-400" />
            <select
              value={startHour}
              onChange={e => setStartHour(parseInt(e.target.value))}
              className="text-[11px] font-semibold bg-transparent outline-none text-slate-600"
              data-testid="start-time-picker"
            >
              {Array.from({ length: 14 }, (_, i) => i + 6).map(h => (
                <option key={h} value={h}>{h > 12 ? `${h - 12}pm` : h === 12 ? "12pm" : `${h}am`}</option>
              ))}
            </select>
          </div>
          <button
            onClick={autoArrange}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-sky-50 text-sky-600 text-[11px] font-semibold hover:bg-sky-100 transition"
            data-testid="auto-arrange-btn"
          >
            <Wand2 className="w-3 h-3" /> Auto
          </button>
          <SchedulerHelpSheet type="smart-queue" />
        </div>
      </div>

      {/* Queue list */}
      <div className="space-y-2">
        {dayStops.length === 0 && (
          <div className="py-8 text-center text-sm text-slate-300">No stops for Day {activeDay}</div>
        )}

        {dayStops.map((stop, idx) => {
          const currentSlot = SLOT_OPTIONS.find(s => s.id === stop.time_slot) || SLOT_OPTIONS[2];
          const SlotIcon = currentSlot.icon;

          return (
            <motion.div
              key={stop.business_id}
              layout
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={`bg-white rounded-xl border shadow-sm overflow-hidden ${stop.locked ? "border-amber-300 ring-1 ring-amber-200" : "border-slate-100"}`}
              data-testid={`queue-stop-${stop.business_id}`}
            >
              <div className="flex items-center gap-2 p-3">
                {/* Reorder arrows */}
                <div className="flex flex-col gap-0.5 flex-shrink-0">
                  <button
                    onClick={() => moveStop(idx, -1)}
                    disabled={idx === 0}
                    className="w-5 h-5 rounded flex items-center justify-center hover:bg-slate-100 disabled:opacity-20"
                  >
                    <GripVertical className="w-3 h-3 text-slate-400 rotate-180" />
                  </button>
                  <button
                    onClick={() => moveStop(idx, 1)}
                    disabled={idx === dayStops.length - 1}
                    className="w-5 h-5 rounded flex items-center justify-center hover:bg-slate-100 disabled:opacity-20"
                  >
                    <GripVertical className="w-3 h-3 text-slate-400" />
                  </button>
                </div>

                {/* Est time */}
                <div className="flex-shrink-0 w-14 text-center">
                  <span className="text-[10px] font-bold text-slate-500">{getEstTime(idx)}</span>
                </div>

                <BrandLogo name={stop.name} logoUrl={stop.logo_url} size="xs" />

                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-slate-800 truncate">{stop.name}</p>
                  <p className="text-[10px] text-slate-400">{stop.category} · ${stop.value_est || 0}</p>
                </div>

                {/* Lock toggle */}
                <button
                  onClick={() => toggleLock(stop.business_id)}
                  className={`w-7 h-7 rounded-lg flex items-center justify-center transition ${stop.locked ? "bg-amber-100 text-amber-600" : "bg-slate-50 text-slate-300 hover:bg-slate-100"}`}
                  data-testid={`lock-${stop.business_id}`}
                  title={stop.locked ? "Locked — won't move during auto-arrange" : "Click to lock position"}
                >
                  {stop.locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
                </button>
              </div>

              {/* Slot picker row */}
              <div className="flex items-center gap-1.5 px-3 pb-2.5">
                {SLOT_OPTIONS.map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => changeSlot(stop.business_id, opt.id)}
                    className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold transition ${stop.time_slot === opt.id ? opt.color : "bg-slate-50 text-slate-400 hover:bg-slate-100"}`}
                    data-testid={`slot-pick-${stop.business_id}-${opt.id}`}
                  >
                    <opt.icon className="w-3 h-3" />
                    {opt.label}
                  </button>
                ))}
                {numDays > 1 && (
                  <select
                    value={stop.day}
                    onChange={e => changeDayForStop(stop.business_id, parseInt(e.target.value))}
                    className="ml-auto text-[10px] bg-slate-50 rounded-lg px-1.5 py-1 font-semibold text-slate-500 outline-none"
                  >
                    {Array.from({ length: numDays }, (_, i) => i + 1).map(d => (
                      <option key={d} value={d}>Day {d}</option>
                    ))}
                  </select>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
